import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Share2, Copy, Check, BarChart3, X, Gift,
  Lock, Unlock, MessageCircle, Loader2, RefreshCw, ScanLine, MessageSquareText,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import QRScannerModal from './QRScannerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PollsManager = ({ onFeedbackSeen }) => {
  const token = localStorage.getItem('token');
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [dealText, setDealText] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [feedbackByPoll, setFeedbackByPoll] = useState({});
  const [openFeedbackId, setOpenFeedbackId] = useState(null);
  const [loadingFeedbackId, setLoadingFeedbackId] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPolls = async () => {
    try {
      const res = await axios.get(`${API}/admin/polls`, authHeader);
      setPolls(res.data);
    } catch (e) {
      toast.error('Failed to load polls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolls();
    // eslint-disable-next-line
  }, []);

  const addOption = () => setOptions([...options, '']);
  const removeOption = (i) => setOptions(options.filter((_, idx) => idx !== i));
  const updateOption = (i, val) => setOptions(options.map((o, idx) => (idx === i ? val : o)));

  const resetForm = () => {
    setQuestion('');
    setOptions(['', '']);
    setDealText('');
    setShowForm(false);
  };

  const createPoll = async () => {
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim()) return toast.error('Enter a question');
    if (clean.length < 2) return toast.error('Add at least 2 options');

    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/polls`,
        { question: question.trim(), options: clean, deal_text: dealText.trim() || null },
        authHeader
      );
      toast.success('Poll created!');
      resetForm();
      fetchPolls();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create poll');
    } finally {
      setCreating(false);
    }
  };

  const pollLink = (id) => `${window.location.origin}/poll/${id}`;

  const copyLink = async (id) => {
    const link = pollLink(id);
    try {
      await navigator.clipboard.writeText(link);
    } catch (e) {
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        toast.error('Could not copy link');
        return;
      }
    }
    setCopiedId(id);
    toast.success('Link copied');
    setTimeout(() => setCopiedId(null), 1500);
  };

  const shareWhatsApp = (poll) => {
    const text = `📊 ${poll.question}\n\nVote here (anonymous): ${pollLink(poll.id)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const toggleStatus = async (id) => {
    try {
      await axios.put(`${API}/admin/polls/${id}/status`, {}, authHeader);
      fetchPolls();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const deletePoll = async (id) => {
    if (!window.confirm('Delete this poll and all its votes?')) return;
    try {
      await axios.delete(`${API}/admin/polls/${id}`, authHeader);
      toast.success('Poll deleted');
      fetchPolls();
    } catch (e) {
      toast.error('Failed to delete poll');
    }
  };

  const toggleFeedback = async (id) => {
    if (openFeedbackId === id) {
      setOpenFeedbackId(null);
      return;
    }
    setOpenFeedbackId(id);
    setLoadingFeedbackId(id);
    try {
      const res = await axios.get(`${API}/admin/polls/${id}/feedback`, authHeader);
      setFeedbackByPoll((prev) => ({ ...prev, [id]: res.data }));
      // Opening feedback marks it read server-side — clear the "new" flag locally & refresh the badge.
      setPolls((prev) => prev.map((p) => (p.id === id ? { ...p, new_feedback_count: 0 } : p)));
      onFeedbackSeen?.();
    } catch (e) {
      toast.error('Failed to load feedback');
      setOpenFeedbackId(null);
    } finally {
      setLoadingFeedbackId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="polls-manager">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-white sm:text-3xl">Polls</h2>
          <p className="mt-1 text-sm text-[#a1a1aa]">Anonymous polls shared via WhatsApp — voters get a QR deal.</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="new-poll-btn"
            onClick={() => setShowForm(!showForm)}
            className="h-11 flex-1 rounded-full bg-[#EAB308] font-semibold text-black hover:bg-[#CA8A04] sm:flex-none sm:px-5"
          >
            <Plus className="w-4 h-4 mr-2" /> New poll
          </Button>
          <Button
            data-testid="scan-qr-btn"
            onClick={() => setShowScanner(true)}
            className="h-11 flex-1 rounded-full bg-[#A855F7] font-semibold text-white hover:bg-[#9333EA] sm:flex-none sm:px-5"
          >
            <ScanLine className="w-4 h-4 mr-2" /> Scan QR
          </Button>
          <Button
            data-testid="polls-refresh-btn"
            onClick={fetchPolls}
            variant="outline"
            className="h-11 w-11 shrink-0 rounded-full border-white/[0.08] p-0 text-[#a1a1aa] hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {showScanner && <QRScannerModal onClose={() => setShowScanner(false)} />}

      {/* Create form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-4 overflow-hidden rounded-2xl border border-white/[0.06] bg-[#18181b] p-4 sm:p-6"
          data-testid="poll-create-form"
        >
          <div>
            <label className="text-sm text-[#a1a1aa] mb-1.5 block">Question</label>
            <Input
              data-testid="poll-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. What should we improve first?"
              className="bg-[#09090b] border-[#27272a] text-white"
            />
          </div>

          <div>
            <label className="text-sm text-[#a1a1aa] mb-1.5 block">Options</label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    data-testid={`poll-option-input-${i}`}
                    value={opt}
                    onChange={(e) => updateOption(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    className="bg-[#09090b] border-[#27272a] text-white"
                  />
                  {options.length > 2 && (
                    <Button
                      data-testid={`remove-option-${i}`}
                      onClick={() => removeOption(i)}
                      variant="outline"
                      className="border-[#27272a] text-[#f43f5e] hover:bg-[#f43f5e]/10 px-3"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              data-testid="add-option-btn"
              onClick={addOption}
              variant="outline"
              className="mt-2 border-[#27272a] text-[#a1a1aa] hover:text-white text-sm"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add option
            </Button>
          </div>

          <div>
            <label className="text-sm text-[#a1a1aa] mb-1.5 block flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5 text-[#EAB308]" /> Reward / Deal (optional)
            </label>
            <Textarea
              data-testid="poll-deal-input"
              value={dealText}
              onChange={(e) => setDealText(e.target.value)}
              placeholder="e.g. 10% off your next order — shown as a QR code after voting"
              className="bg-[#09090b] border-[#27272a] text-white min-h-[70px]"
            />
            <p className="text-xs text-[#52525b] mt-1">If set, voters get a scannable QR code to claim this deal.</p>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              data-testid="submit-poll-btn"
              onClick={createPoll}
              disabled={creating}
              className="bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold rounded-full"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Poll'}
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="border-[#27272a] text-[#a1a1aa] hover:text-white rounded-full"
            >
              Cancel
            </Button>
          </div>
        </motion.div>
      )}

      {/* Poll list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" />
        </div>
      ) : polls.length === 0 ? (
        <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-2xl" data-testid="polls-empty">
          <BarChart3 className="w-10 h-10 text-[#52525b] mx-auto mb-3" />
          <p className="text-[#a1a1aa]">No polls yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="polls-list">
          {polls.map((poll) => (
            <div
              key={poll.id}
              className="rounded-2xl border border-white/[0.06] bg-[#18181b] p-4 sm:p-5"
              data-testid={`poll-card-${poll.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-heading font-semibold">{poll.question}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        poll.status === 'active'
                          ? 'bg-[#10b981]/15 text-[#10b981]'
                          : 'bg-[#52525b]/20 text-[#a1a1aa]'
                      }`}
                    >
                      {poll.status === 'active' ? 'Active' : 'Closed'}
                    </span>
                    {poll.deal_text && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#EAB308]/15 text-[#EAB308] flex items-center gap-1">
                        <Gift className="w-3 h-3" /> Reward
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#52525b] mt-1">{poll.total_votes} total votes</p>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-2.5 mb-4">
                {poll.options.map((opt) => (
                  <div key={opt.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">{opt.text}</span>
                      <span className="text-[#a1a1aa] tabular-nums">
                        {opt.percentage}% <span className="text-[#52525b]">({opt.votes})</span>
                      </span>
                    </div>
                    <div className="h-2 bg-[#27272a] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#A855F7]"
                        style={{ width: `${opt.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[#27272a]">
                <Button
                  data-testid={`share-whatsapp-${poll.id}`}
                  onClick={() => shareWhatsApp(poll)}
                  className="bg-[#25D366] hover:bg-[#1eb658] text-black font-semibold rounded-full text-sm h-9"
                >
                  <MessageCircle className="w-4 h-4 mr-1.5" /> Share
                </Button>
                <Button
                  data-testid={`copy-link-${poll.id}`}
                  onClick={() => copyLink(poll.id)}
                  variant="outline"
                  className="border-[#27272a] text-[#a1a1aa] hover:text-white rounded-full text-sm h-9"
                >
                  {copiedId === poll.id ? (
                    <><Check className="w-4 h-4 mr-1.5 text-[#10b981]" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1.5" /> Copy link</>
                  )}
                </Button>
                <Button
                  data-testid={`toggle-status-${poll.id}`}
                  onClick={() => toggleStatus(poll.id)}
                  variant="outline"
                  className="border-[#27272a] text-[#a1a1aa] hover:text-white rounded-full text-sm h-9"
                >
                  {poll.status === 'active' ? (
                    <><Lock className="w-4 h-4 mr-1.5" /> Close</>
                  ) : (
                    <><Unlock className="w-4 h-4 mr-1.5" /> Reopen</>
                  )}
                </Button>
                <Button
                  data-testid={`view-feedback-${poll.id}`}
                  onClick={() => toggleFeedback(poll.id)}
                  variant="outline"
                  className={`relative rounded-full text-sm h-9 border-[#27272a] ${poll.feedback_count > 0 ? 'text-[#EAB308] hover:bg-[#EAB308]/10' : 'text-[#a1a1aa] hover:text-white'}`}
                >
                  <MessageSquareText className="w-4 h-4 mr-1.5" />
                  Feedback{poll.feedback_count ? ` (${poll.feedback_count})` : ''}
                  {poll.new_feedback_count > 0 && (
                    <span
                      data-testid={`feedback-new-badge-${poll.id}`}
                      className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#f43f5e] px-1 text-[10px] font-bold text-white"
                    >
                      {poll.new_feedback_count}
                    </span>
                  )}
                </Button>
                <Button
                  data-testid={`delete-poll-${poll.id}`}
                  onClick={() => deletePoll(poll.id)}
                  variant="outline"
                  className="border-[#27272a] text-[#f43f5e] hover:bg-[#f43f5e]/10 rounded-full text-sm h-9 ml-auto"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Feedback list */}
              {openFeedbackId === poll.id && (
                <div className="mt-3 border-t border-[#27272a] pt-3" data-testid={`feedback-list-${poll.id}`}>
                  {loadingFeedbackId === poll.id ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-[#EAB308] animate-spin" /></div>
                  ) : (feedbackByPoll[poll.id] || []).length === 0 ? (
                    <p className="text-sm text-[#52525b] py-2 text-center">No written feedback yet.</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-widest text-[#71717a]">Private feedback ({feedbackByPoll[poll.id].length})</p>
                      {feedbackByPoll[poll.id].map((f, i) => (
                        <div key={i} className="rounded-xl bg-[#09090b] border border-[#27272a] p-3">
                          <p className="text-sm text-white whitespace-pre-wrap">{f.feedback}</p>
                          <p className="text-[11px] text-[#52525b] mt-1.5">
                            {f.option_text ? `Voted: ${f.option_text}` : 'Vote recorded'}
                            {f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PollsManager;
